/**
 * src/ai/AiChat.jsx
 *
 * Floating AI support chat widget.
 *
 * ── Props ────────────────────────────────────────────────────────────────
 *
 *   module {Array|null}  The current module state from Layout:
 *                        [moduleCode, submodule]  e.g. ['fin', 'ban']
 *                        null when no module is active (home screen).
 *
 * ── Screen ID ────────────────────────────────────────────────────────────
 *
 *   Built from props.module + subScreen from AiScreenContext:
 *   "fin-ban:banks_reconciliation", "adm-sal:invoices", etc.
 *   Sent to the server so the AI receives the correct screen context.
 *   Format must match the keys in ai/screen-contexts/ on the backend.
 *
 * ── Conversation ──────────────────────────────────────────────────────────
 *
 *   Stateless on the server: the full message history is sent every turn.
 *   The hook (useAiChat) holds the local state.
 *   When the user navigates to a different screen the conversation is kept
 *   so the context does not feel abrupt; the server adapts naturally because
 *   screenId updates on the next message.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useAiChat } from './useAiChat'
import { useAiSubScreen } from './AiScreenContext'
import './AiChat.css'

// ── Icons (inline SVG to avoid extra icon library dependencies) ─────────────

const IconChat = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)

const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6"  x2="6"  y2="18"/>
    <line x1="6"  y1="6"  x2="18" y2="18"/>
  </svg>
)

const IconReset = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10"/>
    <path d="M3.51 15a9 9 0 1 0 .49-3.2"/>
  </svg>
)

const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
  </svg>
)

const IconTicket = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:11,height:11}}>
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z"/>
  </svg>
)

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Derives the screenId from module state and optional sidebar sub-screen.
 * e.g.  ['fin', 'ban'] + "banks_reconciliation" → "fin-ban:banks_reconciliation"
 */
function getScreenId(module, subScreen) {
  if (!Array.isArray(module) || !module[0]) return null
  const parent = module[1] ? `${module[0]}-${module[1]}` : module[0]
  if (subScreen) return `${parent}:${subScreen}`
  return parent
}

// ── Component ────────────────────────────────────────────────────────────────

const AiChat = ({ module }) => {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')

  const { subScreen } = useAiSubScreen()
  const screenId = getScreenId(module, subScreen)
  const { messages, loading, error, send, reset } = useAiChat({ screenId })

  const messagesEndRef = useRef(null)
  const textareaRef    = useRef(null)

  // Scroll to bottom whenever messages change or the panel opens
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, open])

  // Auto-focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 80)
    }
  }, [open])

  const handleSend = () => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')
    send(text)
  }

  const handleKeyDown = (e) => {
    // Send on Enter; allow Shift+Enter for newlines
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleReset = () => {
    reset()
    setInput('')
  }

  return (
    <>
      {/* ── Floating trigger button ── */}
      <button
        className="ai-chat-trigger"
        onClick={() => setOpen(o => !o)}
        title="Asistente Pymego"
        aria-label="Abrir asistente"
      >
        {open ? <IconClose /> : <IconChat />}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div className="ai-chat-panel" role="dialog" aria-label="Asistente de soporte Pymego">

          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-header-info">
              <div className="ai-chat-avatar">🤖</div>
              <div>
                <div className="ai-chat-title">Asistente Pymego</div>
                <div className="ai-chat-subtitle">Soporte del sistema</div>
              </div>
            </div>
            <div className="ai-chat-header-actions">
              {messages.length > 0 && (
                <button
                  className="ai-chat-btn-icon"
                  onClick={handleReset}
                  title="Nueva conversación"
                  aria-label="Nueva conversación"
                >
                  <IconReset />
                </button>
              )}
              <button
                className="ai-chat-btn-icon"
                onClick={() => setOpen(false)}
                title="Cerrar"
                aria-label="Cerrar asistente"
              >
                <IconClose />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="ai-chat-messages" role="log" aria-live="polite">
            {messages.length === 0 && (
              <div className="ai-chat-welcome">
                <div className="ai-chat-welcome-icon">💬</div>
                <h4>¿En qué puedo ayudarte?</h4>
                <p>Preguntame sobre cómo usar cualquier función del sistema.</p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`ai-msg ai-msg--${msg.role}`}
              >
                <div className="ai-msg-bubble">
                  {msg.content}
                </div>
                {msg.ticketCreated && (
                  <div className="ai-msg-ticket-badge">
                    <IconTicket />
                    {msg.ticketId
                      ? `Ticket #${msg.ticketId} derivado a soporte Pymego`
                      : 'Consulta derivada a soporte Pymego'}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="ai-typing" aria-label="El asistente está escribiendo">
                <span /><span /><span />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          {error && (
            <div className="ai-chat-error" role="alert">
              {error}
            </div>
          )}

          {/* Input */}
          <div className="ai-chat-input-area">
            <textarea
              ref={textareaRef}
              className="ai-chat-textarea"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribí tu pregunta..."
              rows={1}
              disabled={loading}
              aria-label="Mensaje para el asistente"
            />
            <button
              className="ai-chat-send"
              onClick={handleSend}
              disabled={!input.trim() || loading}
              aria-label="Enviar mensaje"
            >
              <IconSend />
            </button>
          </div>

        </div>
      )}
    </>
  )
}

export default AiChat
